// Identité de l'usager (D-015) : ce que change la vérification au guichet, informations
// déclarées (date et lieu de naissance, adresse, quartier, ville). Toute modification après une
// vérification l'annule : l'écran le dit et demande une confirmation.
import { Bandeau, Bouton, CaseACocher, Champ, DateChamp, Formulaire } from '@cc/ui';
import { useEffect, useState } from 'react';
import { requete } from '../../../api/client';
import { date } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { EnTeteCitoyen, ExigeConnexion } from '../commun';
import { CLE_PROFIL_USAGER, useSessionUsager } from '../session/FournisseurSessionUsager';
import type { ProfilUsager } from '../types';
import { LIBELLES_PIECE_IDENTITE } from './PageCompte';
import styles from '../citoyen.module.css';

type Identite = { date_naissance: string; lieu_naissance: string; adresse: string; quartier: string; ville: string };

const CHAMPS: { cle: keyof Identite; libelle: string; type?: 'date' }[] = [
  { cle: 'date_naissance', libelle: 'Date de naissance', type: 'date' },
  { cle: 'lieu_naissance', libelle: 'Lieu de naissance' },
  { cle: 'adresse', libelle: 'Adresse' },
  { cle: 'quartier', libelle: 'Quartier' },
  { cle: 'ville', libelle: 'Ville' },
];

function depuisProfil(p: ProfilUsager): Identite {
  const d = (p.identite_declaree ?? {}) as Partial<Record<keyof Identite, string>>;
  return { date_naissance: d.date_naissance ?? '', lieu_naissance: d.lieu_naissance ?? '', adresse: d.adresse ?? '', quartier: d.quartier ?? '', ville: d.ville ?? '' };
}

function FormulaireIdentite({ profil }: { profil: ProfilUsager }) {
  const [valeurs, setValeurs] = useState<Identite>(() => depuisProfil(profil));
  const [confirme, setConfirme] = useState(false);
  useEffect(() => setValeurs(depuisProfil(profil)), [profil]);
  const modifie = JSON.stringify(valeurs) !== JSON.stringify(depuisProfil(profil));
  const enregistrement = useAction({
    executer: () =>
      requete<ProfilUsager>('/usager/profil', {
        methode: 'PUT',
        corps: { identite_declaree: Object.fromEntries(Object.entries(valeurs).filter(([, v]) => v.trim() !== '')) },
      }),
    succes: 'Identité déclarée enregistrée.',
    invalider: [[...CLE_PROFIL_USAGER]],
    apres: () => setConfirme(false),
  });
  const erreurs = enregistrement.erreursChamps.map((e) => ({ champ: `identite-${e.champ.replace(/^identite_declaree\./, '').replace(/_/g, '-')}`, message: e.message }));
  const bloque = Boolean(profil.identite_verifiee) && modifie && !confirme;
  return (
    <Formulaire
      libelle="Identité déclarée"
      onSoumettre={() => {
        if (!bloque) enregistrement.lancer(undefined);
      }}
      erreurs={erreurs}
      actions={
        <Bouton type="submit" variante="secondaire" chargement={enregistrement.enCours} disabled={!modifie || bloque}>
          Enregistrer
        </Bouton>
      }
    >
      {enregistrement.messageErreur && erreurs.length === 0 ? <Bandeau type="erreur">{enregistrement.messageErreur}</Bandeau> : null}
      {CHAMPS.map((c) =>
        c.type === 'date' ? (
          <DateChamp key={c.cle} id={`identite-${c.cle.replace(/_/g, '-')}`} libelle={c.libelle} value={valeurs[c.cle]} onChange={(e) => setValeurs({ ...valeurs, [c.cle]: e.target.value })} erreur={erreurs.find((x) => x.champ === `identite-${c.cle.replace(/_/g, '-')}`)?.message ?? null} facultatif />
        ) : (
          <Champ key={c.cle} id={`identite-${c.cle.replace(/_/g, '-')}`} libelle={c.libelle} value={valeurs[c.cle]} onChange={(e) => setValeurs({ ...valeurs, [c.cle]: e.target.value })} erreur={erreurs.find((x) => x.champ === `identite-${c.cle.replace(/_/g, '-')}`)?.message ?? null} facultatif />
        ),
      )}
      {profil.identite_verifiee && modifie ? (
        <>
          <Bandeau type="attention" titre="La vérification sera annulée">
            Votre identité a été vérifiée au guichet. Modifier ces informations la ramène à « déclarée » : une nouvelle vérification au guichet sera nécessaire pour les démarches qui l&apos;exigent.
          </Bandeau>
          <CaseACocher libelle="Je comprends que la vérification de mon identité sera annulée" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} />
        </>
      ) : null}
    </Formulaire>
  );
}

export function PageIdentite() {
  const { profil } = useSessionUsager();
  return (
    <>
      <EnTeteCitoyen titre="Mon identité" retour="/citoyen/compte" libelleRetour="Mon compte" />
      <ExigeConnexion>
        {profil ? (
          <div className={styles.pile}>
            <section className={styles.carte} aria-labelledby="identite-etat">
              <h2 id="identite-etat" className={styles.blocTitre}>
                {profil.identite_verifiee ? 'Identité vérifiée au guichet' : 'Identité déclarée'}
              </h2>
              {profil.identite_verifiee ? (
                <p>
                  Vérifiée le {date(profil.identite_verifiee.le)}
                  {profil.identite_verifiee.collectivite ? ` par ${profil.identite_verifiee.collectivite}` : ''}, sur présentation d&apos;une {LIBELLES_PIECE_IDENTITE[profil.identite_verifiee.type_piece] ?? profil.identite_verifiee.type_piece}
                  {profil.identite_verifiee.date_piece ? ` datée du ${date(profil.identite_verifiee.date_piece)}` : ''}. Le numéro de la pièce n&apos;est pas conservé.
                </p>
              ) : (
                <p>Les informations ci-dessous sont celles que vous déclarez. Elles suffisent pour la plupart des démarches.</p>
              )}
              <h3>Ce que change la vérification</h3>
              <ul>
                <li>Un agent de la collectivité regarde votre pièce d&apos;identité au guichet et note son type et sa date, jamais son numéro.</li>
                <li>Les démarches marquées « identité vérifiée requise » ne peuvent être déposées qu&apos;après cette vérification.</li>
                <li>Changer votre nom, vos prénoms ou vos informations déclarées annule la vérification.</li>
              </ul>
              {!profil.identite_verifiee ? <p className={styles.aide}>Pour faire vérifier votre identité, présentez-vous au guichet avec une pièce d&apos;identité ; vous pouvez prendre rendez-vous depuis le menu.</p> : null}
            </section>
            <FormulaireIdentite profil={profil} />
          </div>
        ) : null}
      </ExigeConnexion>
    </>
  );
}
